import { useFeatureFlags } from "@app/lib/auth/AuthContext";
import { getBillingCurrencyForCountry } from "@app/lib/plans/billing_currency";
import type { KillSwitchType } from "@app/lib/poke/types";
import { useGeolocation } from "@app/lib/swr/geo";
import type { SupportedCurrency } from "@app/types/currency";
import type { WhitelistableFeature } from "@app/types/shared/feature_flags";
import { useKillSwitches } from "../swr/kill";

// If mention the price of the PRO plan in a few different places in the code base,
// so this is just a way to have that value hardcoded in one place.
// Changing this value only changes the value displayed on the webapp and the website,
// not on the Stripe dashboard.
export const PRO_PLAN_COST_MONTHLY = 29;
export const PRO_PLAN_COST_YEARLY = 27;
export const BUSINESS_PLAN_COST_MONTHLY = 45;

// Credit-priced (CP) self-serve seat prices
export const CP_ENTERPRISE_BASIS = 20;
export const CP_PRO_SEAT_COST_MONTHLY = 30;
export const CP_PRO_SEAT_COST_YEARLY = 24;
export const CP_MAX_SEAT_COST_MONTHLY = 150;
export const CP_MAX_SEAT_COST_YEARLY = 120;

export const DASIS_PRO_SEAT_KRW = { monthly: 49000, yearly: 39000 } as const;
export const DASIS_MAX_SEAT_KRW = { monthly: 245000, yearly: 195000 } as const;
export const DASIS_FREE_SEAT_KRW = { monthly: 0, yearly: 0 } as const;

export function formatKrw(amountKrw: number): string {
  return `₩${amountKrw.toLocaleString("ko-KR")}`;
}

/**
 * Client-side mirror of the server-side `isMetronomeBillingEnabled` gate: the
 * credit-priced checkout flow follows Metronome billing, which is enabled by
 * default for all workspaces. The `global_disable_metronome_billing` kill
 * switch turns it off globally; the `legacy_billing` feature flag forces it
 * off for individual workspaces.
 *
 * Prefer the `useIsMetronomeCheckout` hook; this helper is for components that
 * render outside the auth context provider (e.g. the SPA workspace layout).
 */
export function computeIsMetronomeCheckout({
  featureFlags,
  killSwitches,
}: {
  featureFlags: WhitelistableFeature[];
  killSwitches: KillSwitchType[] | null | undefined;
}): boolean {
  return (
    !featureFlags.includes("legacy_billing") &&
    !killSwitches?.includes("global_disable_metronome_billing")
  );
}

export function useIsMetronomeCheckout(): boolean {
  const { featureFlags } = useFeatureFlags();
  const { killSwitches } = useKillSwitches();
  return computeIsMetronomeCheckout({ featureFlags, killSwitches });
}

export function formatPriceWithCurrency(
  price: number,
  currency: SupportedCurrency
): string {
  return currency === "usd" ? `$${price}` : `${price}€`;
}

/**
 * Hook that resolves the user's billing currency from IP geolocation.
 *
 * When Metronome billing is enabled:
 *   EU/EEA/CH → EUR, rest of world → USD.
 * With legacy billing (Stripe billing, or no workspace):
 *   US → USD, rest of world → EUR (matches Stripe adaptive pricing).
 *
 * Falls back to Stripe behaviour while loading or on error.
 */
export function useUserBillingCurrency(): SupportedCurrency {
  const { geoData } = useGeolocation();
  const { hasFeature } = useFeatureFlags();
  const { killSwitches } = useKillSwitches();
  const isMetronomeBillingEnabled =
    !hasFeature("legacy_billing") &&
    !killSwitches?.includes("global_disable_metronome_billing");

  if (geoData?.countryCode) {
    return getBillingCurrencyForCountry(
      geoData.countryCode,
      isMetronomeBillingEnabled
    );
  }
  // No geo data yet — Stripe default (EUR base, USD for US only).
  return "eur";
}

/**
 * Hook: format a price with the user's billing currency.
 */
export function usePriceWithCurrency(price: number): string {
  const currency = useUserBillingCurrency();
  return formatPriceWithCurrency(price, currency);
}

export interface BillingCycle {
  cycleStart: Date;
  cycleEnd: Date;
}

/**
 * Calculate the billing cycle for a given day of the month.
 * Example: if billing starts on the 4th, the cycle is from the 4th of one month
 * to the 4th of the next month (exclusive).
 *
 * @param billingCycleStartDay - The day of the month when the billing cycle starts (1-31)
 * @param referenceDate - The date to calculate the cycle for (defaults to now)
 * @param useUTC - Whether to use UTC dates (for backend) or local dates (for frontend display)
 */
export function getBillingCycleFromDay(
  billingCycleStartDay: number,
  referenceDate: Date = new Date(),
  useUTC: boolean = false
): BillingCycle {
  const year = useUTC
    ? referenceDate.getUTCFullYear()
    : referenceDate.getFullYear();
  const month = useUTC ? referenceDate.getUTCMonth() : referenceDate.getMonth();
  const day = useUTC ? referenceDate.getUTCDate() : referenceDate.getDate();

  let cycleStart: Date;
  let cycleEnd: Date;

  if (day >= billingCycleStartDay) {
    // Billing cycle started this month, ends next month
    cycleStart = useUTC
      ? new Date(Date.UTC(year, month, billingCycleStartDay, 0, 0, 0, 0))
      : new Date(year, month, billingCycleStartDay);
    cycleEnd = useUTC
      ? new Date(Date.UTC(year, month + 1, billingCycleStartDay, 0, 0, 0, 0))
      : new Date(year, month + 1, billingCycleStartDay);
  } else {
    // Billing cycle started last month, ends this month
    cycleStart = useUTC
      ? new Date(Date.UTC(year, month - 1, billingCycleStartDay, 0, 0, 0, 0))
      : new Date(year, month - 1, billingCycleStartDay);
    cycleEnd = useUTC
      ? new Date(Date.UTC(year, month, billingCycleStartDay, 0, 0, 0, 0))
      : new Date(year, month, billingCycleStartDay);
  }

  return { cycleStart, cycleEnd };
}

/**
 * Calculate the current billing cycle based on the subscription start date.
 * Returns null if no subscription start date is provided.
 */
export function getBillingCycle(
  subscriptionStartDate: number | null,
  referenceDate: Date = new Date()
): BillingCycle | null {
  if (!subscriptionStartDate) {
    return null;
  }

  const billingCycleStartDay = new Date(subscriptionStartDate).getUTCDate();
  return getBillingCycleFromDay(billingCycleStartDay, referenceDate, true);
}

export const getPriceAsString = ({
  currency,
  priceInCents,
  priceInMicroUsd,
}:
  | {
      currency: string;
      priceInCents: number;
      priceInMicroUsd?: undefined;
    }
  | {
      currency: "usd";
      priceInCents?: undefined;
      priceInMicroUsd: number;
    }): string => {
  if (priceInMicroUsd !== undefined) {
    return `$${(priceInMicroUsd / 1_000_000).toFixed(2)}`;
  }

  const price = (priceInCents / 100).toFixed(2);
  switch (currency) {
    case "usd":
      return `$${price}`;
    case "eur":
      return `${price}€`;
    default:
      return `${price}${currency}`;
  }
};
